import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { UserTerminal, TerminalRoutePoint, TerminalTelemetry } from '@/shared/types/userTerminalTypes';
import { PaginatedResponse, SingleResponse } from '@/shared/types/responseTypes';
import { AddL2VPNToTerminalDetails } from '@/shared/types/l2vpnTypes';

/** Infinite scroll fetch for user terminals load-more. */
export const useInfiniteAccountTerminals = (enabled: boolean, hasServiceLine: boolean | undefined, searchQuery: string | undefined) => {
  return useInfiniteQuery({
    queryKey: ['user-terminals-infinite', hasServiceLine, searchQuery],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<UserTerminal>>(
        `/api/v1/user-terminals`,
        { params: { page: pageParam, hasServiceLine, searchQuery } }
      );
      return response.data;
    },
    enabled: enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage ? undefined : lastPageParam + 1,
  });
};

// Use Terminal Query
export const useGetTerminalById = (terminalId: string | undefined, includeRouters: boolean = false) => {
  return useQuery({
    queryKey: ['user-terminal', terminalId],
    queryFn: async () => {
      const response = await apiClient.get<SingleResponse<UserTerminal>>(
        `/api/v1/user-terminals/${terminalId}`,
        {
          params: {
            includeRouters,
            includeActiveAlerts: true,
          },
        }
      );
      return response.data;
    },
    enabled: !!terminalId
  });
};

// Reboot Terminal Mutation
export const useRebootTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ terminalId }: { terminalId: string }) => {
      return await apiClient.post<APIMessageResponse>(
        `/api/v1/user-terminals/${terminalId}/reboot`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rebooted-terminal'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Use Terminal Telemetry Query
export const useTerminalTelemetry = (terminalId: string | undefined, timeRange: string) => {
  return useQuery({
    queryKey: ['user-terminal-telemetry', terminalId, timeRange],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<TerminalTelemetry>>(
        `/api/v1/user-terminals/${terminalId}/telemetry`,
        {
          params: {
            'timeRange': timeRange,
          },
        }
      );
      return response.data.results;
    },
    enabled: !!terminalId
  });
};

function normalizeRoutePoints(results: unknown): TerminalRoutePoint[] {
  if (!Array.isArray(results)) return [];

  return results
    .map((item): TerminalRoutePoint | null => {
      if (typeof item === 'string' && item && item !== '0') {
        return { h3CellId: item, utcTimestamp: '' };
      }
      if (item && typeof item === 'object' && 'h3CellId' in item) {
        const record = item as { h3CellId?: unknown; utcTimestamp?: unknown; timestamp?: unknown };
        const h3CellId = record.h3CellId != null ? String(record.h3CellId) : '';
        if (!h3CellId || h3CellId === '0') return null;
        const utcTimestamp = record.utcTimestamp != null
          ? String(record.utcTimestamp)
          : record.timestamp != null
            ? String(record.timestamp)
            : '';
        return { h3CellId, utcTimestamp };
      }
      return null;
    })
    .filter((item): item is TerminalRoutePoint => item !== null)
    .sort((a, b) => {
      if (a.utcTimestamp && b.utcTimestamp) {
        return new Date(a.utcTimestamp).getTime() - new Date(b.utcTimestamp).getTime();
      }
      return 0;
    });
}

// Use Terminal Route Query
export const useTerminalRoute = (terminalId: string | undefined, timeRange: string = '1d', enabled: boolean = false) => {
  return useQuery({
    queryKey: ['user-terminal-route', terminalId, timeRange],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<TerminalRoutePoint>>(
        `/api/v1/user-terminals/${terminalId}/telemetry/route`,
        {
          params: {
            timeRange,
          },
        }
      );
      return normalizeRoutePoints(response.data.results);
    },
    enabled: !!terminalId && enabled
  });
};

// L2VPN Assignment to Terminal Mutation
export const useAssignL2VPNToTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ terminalId, circuits }: { terminalId: string, circuits: AddL2VPNToTerminalDetails[] }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/user-terminals/${terminalId}/l2vpn-circuits`,
        {
          body: circuits,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['l2vpn-assignment'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};


// Remove L2VPN Assignment from Terminal Mutation
export const useRemoveL2VPNFromTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ terminalId, circuitId }: { terminalId: string, circuitId: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/api/v1/user-terminals/${terminalId}/l2vpn-circuits/${circuitId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['l2vpn-removal'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Transfer Terminal Mutation
export const useTransferTerminal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ terminalId, destinationOrganizationId }: { terminalId: string, destinationOrganizationId: string }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/user-terminals/${terminalId}/transfer`,
        {
          body: { destinationOrganizationId }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-transfer'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Terminal Notes Mutation
export const useTerminalNotes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ terminalId, notes }: { terminalId: string, notes: string | null }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/user-terminals/${terminalId}/notes`,
        {
          body: { notes: notes || null }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-notes'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};